/**
 * Tax Calculation Utilities for New Tax Regime
 * Based on FY 2024-25 New Regime Slabs
 */

// Constants
const STANDARD_DEDUCTION = 75000;
const MAX_NPS_80CCD2 = 50000;

// New Regime Tax Slabs (Budget 2024 July)
const TAX_SLABS = [
    { min: 0, max: 300000, rate: 0 },
    { min: 300000, max: 700000, rate: 0.05 },
    { min: 700000, max: 1000000, rate: 0.10 },
    { min: 1000000, max: 1200000, rate: 0.15 },
    { min: 1200000, max: 1500000, rate: 0.20 },
    { min: 1500000, max: Infinity, rate: 0.30 }
];

export const calculateTotalSalary = (monthlySalary) => {
    return monthlySalary * 12;
};

export const calculateTaxableIncome = (totalSalary, professionalTax = 0) => {
    return totalSalary - STANDARD_DEDUCTION - professionalTax;
};

export const calculateAllowed80CCD2 = (npsValue) => {
    return Math.min(npsValue || 0, MAX_NPS_80CCD2);
};

export const calculateTotalIncome = (taxableIncome, allowed80CCD2) => {
    return Math.max(0, taxableIncome - allowed80CCD2);
};

export const calculateTaxSlabs = (totalIncome) => {
    let totalTax = 0;
    const slabBreakdown = [];

    if (totalIncome > 300000) {
        const taxableInSlab = Math.min(Math.max(0, totalIncome - 300000), 400000);
        if (taxableInSlab > 0) {
            const slabTax = taxableInSlab * 0.05;
            totalTax += slabTax;
            slabBreakdown.push({ slab: '3L - 7L', rate: '5%', income: taxableInSlab, tax: slabTax });
        }
    }
    if (totalIncome > 700000) {
        const taxableInSlab = Math.min(Math.max(0, totalIncome - 700000), 300000);
        if (taxableInSlab > 0) {
            const slabTax = taxableInSlab * 0.10;
            totalTax += slabTax;
            slabBreakdown.push({ slab: '7L - 10L', rate: '10%', income: taxableInSlab, tax: slabTax });
        }
    }
    if (totalIncome > 1000000) {
        const taxableInSlab = Math.min(Math.max(0, totalIncome - 1000000), 200000);
        if (taxableInSlab > 0) {
            const slabTax = taxableInSlab * 0.15;
            totalTax += slabTax;
            slabBreakdown.push({ slab: '10L - 12L', rate: '15%', income: taxableInSlab, tax: slabTax });
        }
    }
    if (totalIncome > 1200000) {
        const taxableInSlab = Math.min(Math.max(0, totalIncome - 1200000), 300000);
        if (taxableInSlab > 0) {
            const slabTax = taxableInSlab * 0.20;
            totalTax += slabTax;
            slabBreakdown.push({ slab: '12L - 15L', rate: '20%', income: taxableInSlab, tax: slabTax });
        }
    }
    if (totalIncome > 1500000) {
        const taxableInSlab = totalIncome - 1500000;
        if (taxableInSlab > 0) {
            const slabTax = taxableInSlab * 0.30;
            totalTax += slabTax;
            slabBreakdown.push({ slab: 'Above 15L', rate: '30%', income: taxableInSlab, tax: slabTax });
        }
    }

    let rebate = 0;
    if (totalIncome <= 700000) {
        rebate = totalTax;
        totalTax = 0;
    }

    return { totalTax: Math.round(totalTax), rebate: Math.round(rebate), slabBreakdown };
};

export const calculateCompleteTax = ({ monthlySalary = 0, nps80CCD2 = 0, professionalTax = 0 }) => {
    const totalSalary = calculateTotalSalary(monthlySalary);
    const taxableIncome = calculateTaxableIncome(totalSalary, professionalTax);
    const allowed80CCD2 = calculateAllowed80CCD2(nps80CCD2);
    const totalIncome = calculateTotalIncome(taxableIncome, allowed80CCD2);
    const { totalTax, rebate, slabBreakdown } = calculateTaxSlabs(totalIncome);
    return { totalSalary, taxableIncome, totalIncome, totalTax, slabBreakdown };
};

export const calculateSalaryTax = ({
    annualGross = 0,
    ded80C = 0,
    ded80D = 0,
    hraExemption = 0,
    homeLoanInterest = 0,
    otherDeductions = 0
}) => {
    const SD_OLD = 50000;
    const SD_NEW = 75000;

    // --- OLD REGIME ---
    const totalDeductionsOld = SD_OLD + Math.min(ded80C, 150000) + Math.min(ded80D, 25000) + hraExemption + Math.min(homeLoanInterest, 200000) + otherDeductions;
    const taxableOld = Math.max(0, annualGross - totalDeductionsOld);

    const calcOld = (t) => {
        let tax = 0;
        if (t > 1000000) { tax += (t - 1000000) * 0.3; t = 1000000; }
        if (t > 500000) { tax += (t - 500000) * 0.2; t = 500000; }
        if (t > 250000) { tax += (t - 250000) * 0.05; }
        if (t <= 500000) return 0; // Rebate 87A
        return tax;
    };

    // --- NEW REGIME ---
    const taxableNew = Math.max(0, annualGross - SD_NEW);
    const calcNew = (t) => {
        let tax = 0;
        if (t > 1500000) { tax += (t - 1500000) * 0.30; t = 1500000; }
        if (t > 1200000) { tax += (t - 1200000) * 0.20; t = 1200000; }
        if (t > 1000000) { tax += (t - 1000000) * 0.15; t = 1000000; }
        if (t > 700000) { tax += (t - 700000) * 0.10; t = 700000; }
        if (t > 300000) { tax += (t - 300000) * 0.05; }
        if (t <= 700000) return 0; // Rebate 87A
        return tax;
    };

    const taxOld = Math.round(calcOld(taxableOld));
    const taxNew = Math.round(calcNew(taxableNew));

    return {
        bestRegime: taxOld <= taxNew ? 'Old Regime' : 'New Regime',
        taxSavings: Math.abs(taxOld - taxNew),
        oldRegime: {
            taxableIncome: taxableOld,
            tax: taxOld,
            standardDeduction: SD_OLD,
            total80C: Math.min(ded80C, 150000),
            totalDeductions: totalDeductionsOld
        },
        newRegime: {
            taxableIncome: taxableNew,
            tax: taxNew,
            standardDeduction: SD_NEW
        }
    };
};

export const calculateBusinessTax = ({ salesTurnover = 0, otherIncome = 0, totalExpenses = 0 }) => {
    const businessProfit = salesTurnover - totalExpenses;
    const totalTaxableIncome = businessProfit + otherIncome;
    const { totalTax, rebate, slabBreakdown } = calculateTaxSlabs(Math.max(0, totalTaxableIncome));
    let cess = 0; // Set to 0 per user instruction in previous version
    return {
        salesTurnover,
        otherIncome,
        totalExpenses,
        businessProfit,
        totalTaxableIncome,
        totalTaxPayable: totalTax + cess,
        cess,
        totalTaxAfterRebate: totalTax,
        slabBreakdown
    };
};

export const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
};

export const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num || 0);
};
